import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';
import { TemplateVersionService } from '../../../../services/template-version.service';
import { NotificationService } from '../../../../services/notification.service';
import { SignalRService } from '../../../../services/signalr.service';
import { AccountInfoService } from '../../../../services/account-info.service';
import {
  TemplateVersion,
  TemplateVersionSummary,
  TemplateVersionDecision,
  TemplateVersionDecisionType,
} from '../../../../models/template-version/template-version.interface';
import { INotification } from '../../../../models/template/notifications-model.interface';
import BlockModel from '../../../../models/email-template/block-model.interface';
import { IUser } from '../../../../models/other/user.interface';

export type ReviewModeV2 = 'off' | 'proof' | 'annotate';

export interface ReviewDraftV2 {
  blockId: string;
  x: number;
  y: number;
}

export interface ReviewStateV2 {
  templateId: string | null;
  language: string;
  mode: ReviewModeV2;
  versions: TemplateVersionSummary[];
  latest: number | null;
  viewing: number | null;
  viewingBlocks: BlockModel[] | null;
  comments: INotification[];
  loadingVersions: boolean;
  activeCommentId: string | null;
  movingCommentId: string | null;
  draft: ReviewDraftV2 | null;
  composerOpen: boolean;
  composerPin: ReviewDraftV2 | null;
}

export const INITIAL_REVIEW_STATE: ReviewStateV2 = {
  templateId: null,
  language: 'en',
  mode: 'off',
  versions: [],
  latest: null,
  viewing: null,
  viewingBlocks: null,
  comments: [],
  loadingVersions: false,
  activeCommentId: null,
  movingCommentId: null,
  draft: null,
  composerOpen: false,
  composerPin: null,
};

@Injectable()
export class ReviewStoreV2 implements OnDestroy {
  private readonly stateSubject = new BehaviorSubject<ReviewStateV2>(INITIAL_REVIEW_STATE);
  readonly state$ = this.stateSubject.asObservable();
  get snapshot(): ReviewStateV2 {
    return this.stateSubject.getValue();
  }

  readonly focusComment$ = new Subject<string>();

  private user: IUser | null = null;
  setUser(user: IUser | null | undefined): void {
    this.user = user ? { name: user.name, username: user.username } : null;
  }

  private readonly blockCache = new Map<string, BlockModel[]>();

  private readonly commentRefresh$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  private disposeLive: () => void = () => {};

  constructor(
    private versionService: TemplateVersionService,
    private notificationService: NotificationService,
    private signalRService: SignalRService,
    accountInfoService: AccountInfoService
  ) {
    // Comments, replies and decisions carry the signed-in user, which MSAL may
    // only resolve after the template has loaded
    accountInfoService.accountInfo$.pipe(takeUntil(this.destroy$)).subscribe((account) => this.setUser(account));

    this.commentRefresh$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshComments();
      });

    this.disposeLive = this.signalRService.onReviewEvents({
      commentsChanged: () => {
        if (this.snapshot.templateId) {
          this.commentRefresh$.next();
        }
      },
      versionsChanged: (templateId, language) => {
        const s = this.snapshot;
        if (s.templateId === templateId && s.language === language) {
          this.refreshVersions();
        }
      },
    });
  }

  private patch(partial: Partial<ReviewStateV2>): void {
    this.stateSubject.next({ ...this.snapshot, ...partial });
  }

  init(templateId: string | null, language: string): void {
    if (templateId === this.snapshot.templateId && language === this.snapshot.language) {
      this.refreshVersions();
      this.refreshComments();
      return;
    }

    this.patch({
      ...INITIAL_REVIEW_STATE,
      mode: this.snapshot.mode,
      templateId,
      language,
    });

    this.refreshVersions();
    this.refreshComments();
  }

  refreshVersions(): void {
    const currentTemplateId = this.snapshot.templateId;
    const currentLanguage = this.snapshot.language;

    if (!currentTemplateId) {
      this.patch({ versions: [], latest: null, viewing: null, viewingBlocks: null });
      return;
    }

    this.patch({ loadingVersions: true });

    this.versionService
      .getVersions(currentTemplateId, currentLanguage, 0, 100)
      .subscribe({
        next: (res) => {
          // Guard against stale response
          if (this.snapshot.templateId !== currentTemplateId || this.snapshot.language !== currentLanguage) {
            return;
          }

          const latest = res[0]?.version ?? null;
          const previousViewing = this.snapshot.viewing;
          const viewing =
            previousViewing !== null && res.some((v) => v.version === previousViewing) ? previousViewing : latest;

          this.patch({ versions: res, latest, viewing, loadingVersions: false });

          if (
            this.snapshot.mode !== 'off' &&
            viewing !== null &&
            (viewing !== previousViewing || this.snapshot.viewingBlocks === null)
          ) {
            this.loadBlocks(viewing);
          }

          if (viewing === null) {
            this.patch({ viewingBlocks: null });
          }
        },
        error: (err) => {
          console.error(err);
          this.patch({ loadingVersions: false });
        },
      });
  }

  refreshComments(): void {
    const currentTemplateId = this.snapshot.templateId;

    if (!currentTemplateId) {
      this.patch({ comments: [] });
      return;
    }

    this.notificationService
      .getTemplateNotifications(currentTemplateId)
      .subscribe({
        next: (res) => {
          // Guard against stale response
          if (this.snapshot.templateId !== currentTemplateId) {
            return;
          }
          this.patch({ comments: res });
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  private loadBlocks(version: number): void {
    const templateId = this.snapshot.templateId;
    const language = this.snapshot.language;
    const key = `${templateId}|${language}|${version}`;
    const cached = this.blockCache.get(key);
    if (cached) {
      this.patch({ viewingBlocks: cached });
      return;
    }

    this.patch({ viewingBlocks: null });

    this.versionService
      .getVersion(templateId!, language, version)
      .subscribe({
        next: (res) => {
          // Guard against stale response
          this.blockCache.set(key, res.blocks);
          if (
            this.snapshot.viewing !== version ||
            this.snapshot.templateId !== templateId ||
            this.snapshot.language !== language
          ) {
            return;
          }
          this.patch({ viewingBlocks: res.blocks });
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  setMode(mode: ReviewModeV2): void {
    if (mode === this.snapshot.mode) return;

    if (mode === 'off') {
      this.patch({
        mode,
        viewing: this.snapshot.latest,
        viewingBlocks: null,
        draft: null,
        movingCommentId: null,
        composerOpen: false,
        composerPin: null,
      });
    } else if (this.snapshot.mode === 'off') {
      this.patch({ mode, viewing: this.snapshot.latest, draft: null });
      if (this.snapshot.latest !== null) {
        this.loadBlocks(this.snapshot.latest);
      }
    } else {
      // proof ↔ annotate
      this.patch({ mode, draft: null, movingCommentId: null });
    }
  }

  selectVersion(version: number): void {
    this.patch({
      viewing: version,
      draft: null,
      movingCommentId: null,
      activeCommentId: null,
      composerOpen: false,
      composerPin: null,
    });
    if (this.snapshot.mode !== 'off') {
      this.loadBlocks(version);
    }
  }

  createVersion(comment: string | null): Observable<TemplateVersion> {
    if (!this.snapshot.templateId) {
      return throwError(() => new Error('Template is not saved yet.'));
    }

    return this.versionService.createVersion({
      templateId: this.snapshot.templateId,
      language: this.snapshot.language,
      comment,
      userInfo: this.user,
    }).pipe(
      tap((v) => {
        this.blockCache.set(`${this.snapshot.templateId}|${this.snapshot.language}|${v.version}`, v.blocks);
        this.patch({
          mode: this.snapshot.mode === 'off' ? 'proof' : this.snapshot.mode,
        });
        this.selectVersion(v.version);
        this.refreshVersions();
      })
    );
  }

  restoreVersion(version: number, comment: string | null): Observable<TemplateVersion> {
    const templateId = this.snapshot.templateId;
    const language = this.snapshot.language;

    return this.versionService.restoreVersion({
      templateId: templateId!,
      language,
      version,
      comment,
      userInfo: this.user,
    }).pipe(
      tap((v) => {
        this.blockCache.set(`${templateId}|${language}|${v.version}`, v.blocks);
        this.selectVersion(v.version);
        this.refreshVersions();
      })
    );
  }

  addDecision(type: TemplateVersionDecisionType, comment: string | null): Observable<TemplateVersionDecision> {
    return this.versionService.addDecision({
      templateId: this.snapshot.templateId!,
      language: this.snapshot.language,
      version: this.snapshot.viewing!,
      type,
      comment,
      userInfo: this.user,
    }).pipe(
      tap(() => {
        this.refreshVersions();
      })
    );
  }

  addComment(
    text: string,
    blockId: string,
    pin: { x: number; y: number } | null
  ): Observable<INotification> {
    return this.notificationService.createNotification({
      templateId: this.snapshot.templateId!,
      blockId,
      userInfo: this.user ?? {},
      notificationText: text,
      xCords: 0,
      yCords: 0,
      pinId: '',
      createdDate: '',
      modifiedDate: '',
      version: this.snapshot.latest,
      xPercent: pin?.x ?? null,
      yPercent: pin?.y ?? null,
    }).pipe(
      tap(() => {
        this.refreshComments();
        this.patch({ draft: null, composerOpen: false, composerPin: null });
      })
    );
  }

  editComment(comment: INotification, text: string): Observable<INotification> {
    return this.notificationService.updateNotification(comment.notificationId!, {
      notificationText: text,
      userInfo: this.user ?? undefined,
    }).pipe(
      tap(() => {
        this.refreshComments();
      })
    );
  }

  deleteComment(comment: INotification): Observable<any> {
    return this.notificationService.deleteNotification(comment.notificationId!, comment.blockId).pipe(
      tap(() => {
        this.refreshComments();
      })
    );
  }

  toggleDone(comment: INotification): Observable<INotification> {
    return this.notificationService.setCompleted(comment.notificationId!, {
      completed: !comment.completed,
      userInfo: this.user ?? {},
    }).pipe(
      tap(() => {
        this.refreshComments();
      })
    );
  }

  movePin(
    comment: INotification,
    blockId: string,
    x: number,
    y: number
  ): Observable<INotification> {
    return this.notificationService.updatePin(comment.notificationId!, {
      blockId,
      xPercent: x,
      yPercent: y,
      userInfo: this.user ?? {},
    }).pipe(
      tap(() => {
        this.refreshComments();
        this.patch({ movingCommentId: null });
      })
    );
  }

  addReply(comment: INotification, text: string): Observable<INotification> {
    return this.notificationService.createReply(comment.notificationId!, {
      replyText: text,
      userInfo: this.user ?? {},
    }).pipe(
      tap(() => {
        this.refreshComments();
      })
    );
  }

  editReply(comment: INotification, replyId: string, text: string): Observable<INotification> {
    return this.notificationService.updateReply(comment.notificationId!, replyId, {
      replyText: text,
      userInfo: this.user ?? {},
    }).pipe(
      tap(() => {
        this.refreshComments();
      })
    );
  }

  deleteReply(comment: INotification, replyId: string): Observable<any> {
    return this.notificationService.deleteReply(comment.notificationId!, replyId).pipe(
      tap(() => {
        this.refreshComments();
      })
    );
  }

  openDraft(blockId: string, x: number, y: number): void {
    this.patch({ draft: { blockId, x, y } });
  }

  cancelDraft(): void {
    this.patch({ draft: null });
  }

  /** Opening the panel composer lets a canvas click place its pin; closing it drops the pin. */
  setComposerOpen(open: boolean): void {
    this.patch({ composerOpen: open, composerPin: null, draft: null });
  }

  setComposerPin(blockId: string, x: number, y: number): void {
    this.patch({ composerPin: { blockId, x, y } });
  }

  clearComposerPin(): void {
    this.patch({ composerPin: null });
  }

  startPinMove(commentId: string): void {
    this.patch({ movingCommentId: commentId, draft: null, composerPin: null });
  }

  cancelPinMove(): void {
    this.patch({ movingCommentId: null });
  }

  focusComment(commentId: string): void {
    this.patch({ activeCommentId: commentId });
    this.focusComment$.next(commentId);
  }

  get viewingVersion(): TemplateVersionSummary | null {
    return this.snapshot.versions.find(v => v.version === this.snapshot.viewing) ?? null;
  }

  ngOnDestroy(): void {
    this.disposeLive();
    this.destroy$.next();
    this.destroy$.complete();
    this.stateSubject.complete();
    this.commentRefresh$.complete();
    this.focusComment$.complete();
  }
}
